/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { area as turfArea, intersect } from '@turf/turf';
import type {
  Feature,
  FeatureCollection,
  MultiPolygon,
  Polygon,
} from 'geojson';
import { dateUtils } from '@opengeoweb/shared';
import { getDefaultValues } from './types';
import type {
  Area,
  WarningEventBody,
  WarningEvent,
} from '../../../store/warningsForm/types';
import { PublicWarningStatus } from '../../../store/warningsForm/types';

// Earliest validFrom and latest validUntil across all warnings
const getEventValidity = (
  warnings: WarningEventBody[],
): Pick<WarningEvent, 'validFrom' | 'validUntil'> =>
  warnings.reduce<Pick<WarningEvent, 'validFrom' | 'validUntil'>>(
    (validity, warning) => ({
      validFrom:
        !validity.validFrom ||
        dateUtils.isBefore(
          dateUtils.isoStringToDate(warning.validFrom)!,
          dateUtils.isoStringToDate(validity.validFrom)!,
        )
          ? warning.validFrom
          : validity.validFrom,
      validUntil:
        !validity.validUntil ||
        dateUtils.isAfter(
          dateUtils.isoStringToDate(warning.validUntil)!,
          dateUtils.isoStringToDate(validity.validUntil)!,
        )
          ? warning.validUntil
          : validity.validUntil,
    }),
    { validFrom: '', validUntil: '' },
  );

// Single source of truth for the fields each warning row inherits from the parent event
const getInheritedEventFields = (
  values: WarningEvent,
): Pick<WarningEventBody, 'incident' | 'phenomenon'> => ({
  incident: values.incident,
  phenomenon: values.phenomenon,
});

interface FormData extends WarningEvent {
  IS_DRAFT?: boolean | string;
}

// FAKE area that is needed for the BE PUBLISH until we implement the real area functionality
const FAKE_AREAS = [
  {
    objectName: 'Drawing name',
    geoJSON: {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [
              [
                [4.62825, 53.0063],
                [4.82904, 53.35734],
                [5.78279, 53.53676],
                [4.8625, 52.63206],
                [4.47765, 52.60158],
                [4.62825, 53.0063],
              ],
            ],
          },
          properties: {
            fill: '#fae21e',
            stroke: '#fae21e',
            'fill-opacity': 0.2,
            'stroke-width': 2,
            selectionType: 'poly',
            warning_level: '1',
            'stroke-opacity': 1,
            objectName: 'Feature name',
          },
        },
      ],
    },
  },
];

export const buildSubmitValues = (values: FormData): WarningEvent => {
  const { IS_DRAFT, ...productToPost } = values;
  const inherited = getInheritedEventFields(productToPost);

  const eventToPost = {
    ...productToPost,
    ...getEventValidity(productToPost.warnings),
    warnings: productToPost.warnings.map((warning) => {
      const joinedWarning = {
        ...warning,
        ...inherited,
        domain: 'public',
        // TODO REMOVE WHEN WE IMPLEMENT THE REAL AREAS FUNCTIONALITY
        areas: FAKE_AREAS,
      } as WarningEventBody;
      // Remove empty fields from all warnings
      return Object.keys(joinedWarning).reduce<WarningEventBody>(
        (warningDetail: WarningEventBody, fieldName: string) => {
          const key = fieldName as keyof WarningEventBody;
          const fieldValue = joinedWarning[key];
          if (Array.isArray(fieldValue)) {
            if (!fieldValue.length || fieldValue[0].geoJSON === null) {
              return warningDetail;
            }
          }
          if (fieldValue) {
            return {
              ...warningDetail,
              [key]: fieldValue,
            };
          }

          return warningDetail;
        },
        {} as WarningEventBody,
      );
    }),
  };

  // remove empty event fields
  return Object.keys(eventToPost).reduce<WarningEvent>(
    (event: WarningEvent, fieldName: string) => {
      const key = fieldName as keyof WarningEvent;
      const fieldValue = eventToPost[key];
      if (Array.isArray(fieldValue) && !fieldValue.length) {
        return event;
      }
      if (fieldValue) {
        return {
          ...event,
          [key]: fieldValue,
        };
      }

      return event;
    },
    {} as WarningEvent,
  );
};

export const getFormData = (
  warningsEvent: WarningEvent | undefined,
  // object?: DrawingListItem,
): FormData => {
  const data = {
    IS_DRAFT: '',
    ...(warningsEvent
      ? {
          ...getDefaultValues(),
          ...warningsEvent,
        }
      : getDefaultValues()),
    // object,
  };

  return data;
};

export const constructWarningWithNewStatus = (
  warningsEvent: WarningEvent,
  status: PublicWarningStatus,
): WarningEventBody[] => {
  return warningsEvent.warnings.map((warning: WarningEventBody) => {
    // Skip any warnings with status EXPIRED or WITHDRAWN
    if (
      warning.status === PublicWarningStatus.EXPIRED ||
      warning.status === PublicWarningStatus.WITHDRAWN
    ) {
      return warning;
    }
    return {
      ...warning,
      status,
    } as WarningEventBody;
  });
};

// Minimum intersection area (in square meters) to count as a real overlap,
// as opposed to two areas merely touching along a shared edge/point (which
// is not considered an overlap).
const MIN_OVERLAP_AREA_M2 = 1;

const isPolygonalFeature = (
  featureToCheck: GeoJSON.Feature,
): featureToCheck is Feature<Polygon | MultiPolygon> =>
  featureToCheck.geometry?.type === 'Polygon' ||
  featureToCheck.geometry?.type === 'MultiPolygon';

const doFeaturesOverlap = (
  featureA: Feature<Polygon | MultiPolygon>,
  featureB: Feature<Polygon | MultiPolygon>,
): boolean => {
  const pair: FeatureCollection<Polygon | MultiPolygon> = {
    type: 'FeatureCollection',
    features: [featureA, featureB],
  };
  const intersection = intersect(pair);
  return !!intersection && turfArea(intersection) > MIN_OVERLAP_AREA_M2;
};

const hasAreaData = (areas?: Area[]): boolean =>
  !!areas?.some((area) => !!area.geoJSON?.features?.length);

export const doAreasOverlap = (areasA?: Area[], areasB?: Area[]): boolean => {
  if (!hasAreaData(areasA) || !hasAreaData(areasB)) {
    return true;
  }

  return areasA!.some((areaA) =>
    areaA.geoJSON.features
      .filter(isPolygonalFeature)
      .some((featureA) =>
        areasB!.some((areaB) =>
          areaB.geoJSON.features
            .filter(isPolygonalFeature)
            .some((featureB) => doFeaturesOverlap(featureA, featureB)),
        ),
      ),
  );
};

export const doTimeRangesOverlap = (
  aFrom: string,
  aUntil: string,
  bFrom: string,
  bUntil: string,
): boolean =>
  dateUtils.unixFromISOString(aFrom) < dateUtils.unixFromISOString(bUntil) &&
  dateUtils.unixFromISOString(bFrom) < dateUtils.unixFromISOString(aUntil);

// Two warnings conflict when they share the same level AND overlap in BOTH
// time and area at the same time.
export const doWarningsOverlap = (
  warningA: WarningEventBody,
  warningB: WarningEventBody,
): boolean =>
  warningA.level === warningB.level &&
  doTimeRangesOverlap(
    warningA.validFrom,
    warningA.validUntil,
    warningB.validFrom,
    warningB.validUntil,
  ) &&
  doAreasOverlap(warningA.areas, warningB.areas);

export const getOverlappingWarnings = (
  warnings: WarningEventBody[],
  index: number,
): number[] => {
  const target = warnings[index];
  if (!target) {
    return [];
  }

  return warnings.reduce<number[]>((overlaps, warning, i) => {
    if (i !== index && doWarningsOverlap(target, warning)) {
      overlaps.push(i);
    }
    return overlaps;
  }, []);
};
